import { createContext, h } from 'preact';
import { useContext, useEffect, useMemo, useState } from 'preact/hooks';
import { getSupabase } from '../lib/supabaseClient';
import { getSupabaseConfig } from '../config';

export type SessionState = {
  isLoading: boolean;
  isSignedIn: boolean;
  profileId: string | null;
  familyId: string | null;
  familyDisplayName: string;
  refresh(): Promise<void>;
  signOut(): Promise<void>;
};

const SessionCtx = createContext<SessionState | null>(null);

export function SessionProvider(props: { children: preact.ComponentChildren }) {
  const [state, setState] = useState<SessionState>({
    isLoading: true,
    isSignedIn: false,
    profileId: null,
    familyId: null,
    familyDisplayName: 'The Careys',
    refresh: async () => {},
    signOut: async () => {},
  });

  useEffect(() => {
    let unsub: (() => void) | null = null;
    let mounted = true;
    const { url, anonKey } = getSupabaseConfig();
    // Allow previews without backend to render using the default stub session
    if (!url || !anonKey) return;

    (async () => {
      async function load() {
        const sb = getSupabase();
        if (!sb) {
          if (!mounted) return;
          setState((s) => ({ ...s, isLoading: false }));
          return;
        }
        // Fast path: use session (works offline)
        const { data: sessRes } = await sb.auth.getSession();
        const sess = sessRes.session;
        if (!sess) {
          if (!mounted) return;
          setState((s) => ({
            ...s,
            isLoading: false,
            isSignedIn: false,
            profileId: null,
            familyId: null,
            familyDisplayName: 'The Careys',
            refresh,
            signOut,
          }));
          return;
        }
        const userId = sess.user.id;
        // Background: fetch profile+family; tolerate network errors
        try {
          const { data: prof } = await sb
            .from('profiles')
            .select('id, family_id')
            .eq('id', userId)
            .single();
          let familyName = 'Your family';
          let fid: string | null = null;
          if (prof?.family_id) {
            fid = prof.family_id as string;
            const { data: fam } = await sb
              .from('families')
              .select('display_name')
              .eq('id', fid)
              .single();
            if (fam?.display_name) familyName = fam.display_name as string;
          }
          if (!mounted) return;
          setState({
            isLoading: false,
            isSignedIn: true,
            profileId: userId,
            familyId: fid,
            familyDisplayName: familyName,
            refresh,
            signOut,
          });
        } catch {
          if (!mounted) return;
          // Keep signed-in minimal state on failure
          setState({
            isLoading: false,
            isSignedIn: true,
            profileId: userId,
            familyId: null,
            familyDisplayName: 'Your family',
            refresh,
            signOut,
          });
        }
      }

      function refresh() {
        return load();
      }
      async function signOut() {
        const sb2 = getSupabase();
        if (sb2) await sb2.auth.signOut();
        await load();
      }
      // Bind functions now that they are defined
      const refreshBound = async () => refresh();
      const signOutBound = async () => signOut();
      // Write bound methods once and keep them stable
      setState((s) => ({ ...s, refresh: refreshBound, signOut: signOutBound }));

      await load();
      if (!mounted) return;
      const s2 = getSupabase();
      if (s2) {
        const subscription = s2.auth.onAuthStateChange((_e) => {
          void load();
        }).data?.subscription;
        if (subscription) {
          unsub = () => {
            try {
              subscription.unsubscribe();
            } catch {
              // ignore
            }
          };
        }
      }
    })();
    return () => {
      mounted = false;
      if (unsub) try { unsub(); } catch {}
    };
  }, []);

  const value = useMemo(() => state, [state]);
  return h(SessionCtx.Provider, { value }, props.children as any);
}

export function useSession(): SessionState {
  const ctx = useContext(SessionCtx);
  if (ctx) return ctx;
  // Fallback for environments without a configured backend
  return {
    isLoading: false,
    isSignedIn: false,
    profileId: null,
    familyId: null,
    familyDisplayName: 'The Careys',
    refresh: async () => {},
    signOut: async () => {},
  };
}


